import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import {
  Users, Phone, RefreshCw, AlertTriangle, CheckCircle2,
  XCircle, Clock, ShieldCheck, ShieldAlert, ChevronDown, ChevronUp,
  UserCheck, PhoneCall, History
} from 'lucide-react';
import { axiosInstance } from '@/App';
import { toast } from 'sonner';

// ─── VERIFICATION STATUSES ──────────────────────────────────────────────────

const VERIFY_STATUSES = [
  { value: 'confirmed', label: 'Confirmed Closing', icon: CheckCircle2, color: 'text-green-400', bg: 'bg-green-900/50 text-green-300 border-green-700' },
  { value: 'already_closed', label: 'Already Closed', icon: ShieldCheck, color: 'text-blue-400', bg: 'bg-blue-900/50 text-blue-300 border-blue-700' },
  { value: 'not_closing', label: 'Not Closing', icon: XCircle, color: 'text-red-400', bg: 'bg-red-900/50 text-red-300 border-red-700' },
  { value: 'not_reachable', label: 'Not Reachable', icon: Clock, color: 'text-amber-400', bg: 'bg-amber-900/50 text-amber-300 border-amber-700' },
  { value: 'wrong_number', label: 'Wrong Number', icon: ShieldAlert, color: 'text-slate-400', bg: 'bg-slate-700/50 text-slate-300 border-slate-600' },
  { value: 'fake_closure', label: 'Fake Closure', icon: AlertTriangle, color: 'text-orange-400', bg: 'bg-orange-900/50 text-orange-300 border-orange-700' },
];

const getVerifyBadge = (status) => {
  const found = VERIFY_STATUSES.find(s => s.value === status);
  if (!found) return null;
  const Icon = found.icon;
  return (
    <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded border ${found.bg}`}>
      <Icon className="w-3 h-3" />
      {found.label}
    </span>
  );
};

const formatVerifiedTime = (isoStr) => {
  if (!isoStr) return '';
  try {
    const d = new Date(isoStr);
    return d.toLocaleString('en-IN', {
      hour: 'numeric', minute: '2-digit', hour12: true,
      day: 'numeric', month: 'short',
      timeZone: 'Asia/Kolkata',
    }).replace(',', ','); // "10:30 am, 16 Mar" → normalize
  } catch { return ''; }
};

// ─── LEAD ROW ───────────────────────────────────────────────────────────────

const LeadVerifyRow = ({ lead, idx, gym, date, onVerified }) => {
  const [expanded, setExpanded] = useState(false);
  const [status, setStatus] = useState('');
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [showHistory, setShowHistory] = useState(false);

  const hasVerification = !!lead.verification;
  const isNotReachable = lead.verification?.status === 'not_reachable';
  const verificationHistory = lead.verification?.history || [];

  const handleSubmit = async () => {
    if (!status) return;
    setSubmitting(true);
    try {
      await axiosInstance.post(
        `/closure-expected/${gym.gym_id}/leads/verify`,
        { phone: lead.phone, name: lead.name, status, comment: comment.trim() },
        { params: { date } }
      );
      toast.success(`${lead.name || 'Lead'} verified as ${status.replace(/_/g, ' ')}`);
      setExpanded(false);
      setStatus('');
      setComment('');
      if (onVerified) onVerified();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Verification failed');
    } finally {
      setSubmitting(false);
    }
  };

  const FOLLOWUP_LABELS = {
    closure_expected: 'Closure Expected',
    interested: 'Interested',
    follow_up: 'Follow Up',
    not_interested: 'Not Interested',
    lost: 'Lost',
    converted: 'Converted',
    pending: 'Pending',
  };

  const getFollowupLabel = (s) => {
    if (!s) return '—';
    return FOLLOWUP_LABELS[s.toLowerCase()] || s.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  };

  const getFollowupBadge = (s) => {
    if (!s) return 'bg-slate-800 text-slate-500 border-slate-700';
    const sl = s.toLowerCase();
    if (sl.includes('closure') || sl.includes('interested') || sl.includes('converted')) return 'bg-green-900/40 text-green-300 border-green-800';
    if (sl.includes('follow') || sl.includes('pending')) return 'bg-amber-900/40 text-amber-300 border-amber-800';
    if (sl.includes('not') || sl.includes('lost')) return 'bg-red-900/40 text-red-300 border-red-800';
    return 'bg-slate-800 text-slate-400 border-slate-700';
  };

  return (
    <>
      <tr className={`border-b border-slate-700/40 transition-colors ${hasVerification ? 'bg-slate-800/20' : 'hover:bg-slate-800/40'}`}>
        <td className="px-3 py-2.5 text-slate-500 text-xs font-mono">{idx + 1}</td>
        <td className="px-3 py-2.5">
          <div className="font-medium text-slate-200 text-sm">{lead.name || '—'}</div>
          {lead.email && <div className="text-[11px] text-slate-500 mt-0.5">{lead.email}</div>}
        </td>
        <td className="px-3 py-2.5">
          <span className="inline-flex items-center gap-1.5 font-mono text-sm text-slate-300">
            <Phone className="w-3 h-3 text-slate-500 flex-shrink-0" />
            {lead.phone || '—'}
          </span>
        </td>
        <td className="px-3 py-2.5">
          <span className="text-sm text-slate-300">{lead.assigned_to || '—'}</span>
        </td>
        <td className="px-3 py-2.5">
          <span className="text-[11px] text-slate-500">{lead.date_added ? lead.date_added.split(' ')[0] : '—'}</span>
        </td>
        <td className="text-right px-3 py-2.5">
          <span className={`text-xs font-mono tabular-nums ${lead.lead_value > 0 ? 'text-emerald-400' : 'text-slate-700'}`}>
            {lead.lead_value > 0 ? `${(lead.lead_value/1000).toFixed(1)}K` : '—'}
          </span>
        </td>
        <td className="px-3 py-2.5">
          <span className={`inline-block text-[11px] px-2 py-0.5 rounded border ${getFollowupBadge(lead.followup_status)}`}>
            {getFollowupLabel(lead.followup_status)}
          </span>
        </td>
        <td className="text-center px-3 py-2.5">
          <span className="font-mono text-sm text-slate-400">{lead.outgoing_calls || 0}</span>
        </td>
        <td className="text-center px-3 py-2.5">
          <span className={`font-mono text-sm font-semibold ${lead.connected_calls > 0 ? 'text-green-400' : 'text-slate-600'}`}>
            {lead.connected_calls || 0}
          </span>
        </td>
        <td className="px-3 py-2.5 text-right">
          {hasVerification ? (
            <div className="flex flex-col items-end gap-1">
              {getVerifyBadge(lead.verification.status)}
              {/* Timestamp + who verified */}
              {lead.verification.verified_at && (
                <span className="text-[10px] text-slate-500 leading-tight">
                  {lead.verification.verified_by_name ? `${lead.verification.verified_by_name} · ` : ''}
                  {formatVerifiedTime(lead.verification.verified_at)}
                </span>
              )}
              {/* Retry button for not_reachable */}
              {isNotReachable && !expanded && (
                <Button
                  variant="outline"
                  size="sm"
                  className="text-[10px] h-6 px-2 mt-0.5 bg-amber-950/40 border-amber-700/60 text-amber-300 hover:bg-amber-900/50 hover:text-amber-200 hover:border-amber-600"
                  onClick={() => setExpanded(true)}
                >
                  <RefreshCw className="w-2.5 h-2.5 mr-1" />
                  Retry — Customer Not Reached
                </Button>
              )}
              {/* Verification history */}
              {verificationHistory.length > 0 && (
                <div className="mt-0.5">
                  <button
                    onClick={() => setShowHistory(!showHistory)}
                    className="text-[10px] text-slate-600 hover:text-slate-400 flex items-center gap-0.5 transition-colors"
                  >
                    <History className="w-2.5 h-2.5" />
                    {showHistory ? 'Hide' : `Previous attempts (${verificationHistory.length})`}
                  </button>
                  {showHistory && (
                    <div className="mt-1 space-y-0.5 text-right">
                      {verificationHistory.map((h, hi) => (
                        <div key={hi} className="text-[10px] text-slate-600 leading-tight">
                          {VERIFY_STATUSES.find(s => s.value === h.status)?.label || h.status}
                          {h.verified_at ? ` · ${formatVerifiedTime(h.verified_at)}` : ''}
                          {h.verified_by_name ? ` · ${h.verified_by_name}` : ''}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          ) : (
            <Button
              variant="outline"
              size="sm"
              className="text-xs h-7 px-2.5 bg-slate-800 border-slate-600 text-slate-300 hover:bg-slate-700 hover:text-slate-100 hover:border-slate-500"
              onClick={() => setExpanded(!expanded)}
            >
              {expanded ? <ChevronUp className="w-3 h-3 mr-1" /> : <ChevronDown className="w-3 h-3 mr-1" />}
              Verify
            </Button>
          )}
        </td>
      </tr>
      {expanded && (!hasVerification || isNotReachable) && (
        <tr className="bg-slate-800/60">
          <td colSpan={10} className="px-3 py-3">
            <div className="flex items-center gap-2.5 flex-wrap">
              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger className="w-48 h-8 text-xs bg-slate-900 border-slate-600 text-slate-200">
                  <SelectValue placeholder="Select status..." />
                </SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-700">
                  {VERIFY_STATUSES.map(s => (
                    <SelectItem key={s.value} value={s.value} className="text-slate-200 focus:bg-slate-800 focus:text-slate-100">
                      <span className="flex items-center gap-1.5">
                        <s.icon className={`w-3 h-3 ${s.color}`} />
                        {s.label}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Textarea
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder="Optional note..."
                className="h-8 min-h-[32px] text-xs resize-none flex-1 min-w-[120px] bg-slate-900 border-slate-600 text-slate-200 placeholder:text-slate-500"
                rows={1}
              />
              <Button
                size="sm"
                className="h-8 text-xs bg-green-700 hover:bg-green-600 text-white border-0"
                disabled={!status || submitting}
                onClick={handleSubmit}
              >
                {submitting ? 'Saving...' : 'Save'}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="h-8 text-xs text-slate-400 hover:text-slate-200 hover:bg-slate-700"
                onClick={() => { setExpanded(false); setStatus(''); setComment(''); }}
              >
                Cancel
              </Button>
            </div>
          </td>
        </tr>
      )}
    </>
  );
};

// ─── MAIN DIALOG ────────────────────────────────────────────────────────────

const CustomerListDialog = ({ open, onOpenChange, gym, date }) => {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [totalLeads, setTotalLeads] = useState(0);

  const fetchLeads = useCallback(async () => {
    if (!gym) return;
    setLoading(true);
    setError(null);
    try {
      const res = await axiosInstance.get(`/closure-expected/${gym.gym_id}/leads`, {
        params: { date },
      });
      // Deduplicate leads by phone (WTF API sometimes returns duplicates)
      const raw = res.data?.data || [];
      const seen = new Set();
      const deduped = raw.filter(l => {
        const key = l.phone || `${l.name}-${l.date_added}`;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
      setLeads(deduped);
      setTotalLeads(res.data?.total_leads || deduped.length);
    } catch (err) {
      const msg = err.response?.data?.detail || err.response?.data?.message || 'Failed to fetch leads';
      setError(msg);
      setLeads([]);
    } finally {
      setLoading(false);
    }
  }, [gym, date]);

  useEffect(() => {
    if (open && gym) {
      fetchLeads();
    }
    if (!open) {
      setLeads([]);
      setError(null);
    }
  }, [open, gym, date, fetchLeads]);

  if (!gym) return null;

  const tlCommitted = gym.tl_committed || 0;
  const verified = leads.filter(l => l.verification).length;
  const confirmedCount = leads.filter(l => l.verification?.status === 'confirmed' || l.verification?.status === 'already_closed').length;
  const notReachableCount = leads.filter(l => l.verification?.status === 'not_reachable').length;
  const negativeCount = leads.filter(l => ['not_closing', 'wrong_number', 'fake_closure'].includes(l.verification?.status)).length;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-6xl max-h-[85vh] overflow-hidden flex flex-col p-0 bg-slate-900 border border-slate-700/80 shadow-2xl shadow-black/50 [&>button]:text-slate-400 [&>button]:hover:text-slate-200">
        {/* ── Header ── */}
        <DialogHeader className="px-5 pt-5 pb-0 space-y-0">
          <div className="flex items-center justify-between">
            <DialogTitle className="flex items-center gap-2.5 text-slate-100 text-base font-semibold">
              <div className="p-1.5 rounded-md bg-blue-500/10 border border-blue-500/20">
                <Users className="h-4 w-4 text-blue-400" />
              </div>
              Closure Expected — Lead Verification
            </DialogTitle>
            <Button
              variant="ghost"
              size="sm"
              onClick={fetchLeads}
              disabled={loading}
              className="h-8 w-8 p-0 text-slate-400 hover:text-slate-200 hover:bg-slate-800"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </div>

          {/* Gym info line */}
          <div className="text-sm text-slate-400 pt-1.5 pb-3">
            <span className="text-slate-200 font-medium">{gym.gym_name}</span>
            <span className="mx-2 text-slate-600">·</span>
            <span>{date}</span>
          </div>

          {/* Stats bar */}
          {!loading && !error && (
            <div className="flex items-center gap-2 pb-4 flex-wrap">
              <div className="flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-md bg-slate-800/80 border border-slate-700/60">
                <PhoneCall className="w-3 h-3 text-blue-400" />
                <span className="text-slate-400">WTF Leads</span>
                <span className="font-semibold text-blue-400 ml-0.5">{totalLeads}</span>
              </div>
              {tlCommitted > 0 && (
                <div className="flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-md bg-slate-800/80 border border-slate-700/60">
                  <Users className="w-3 h-3 text-slate-400" />
                  <span className="text-slate-400">TL Committed</span>
                  <span className="font-semibold text-slate-200 ml-0.5">{tlCommitted}</span>
                </div>
              )}
              {tlCommitted > 0 && tlCommitted !== totalLeads && totalLeads > 0 && (
                <div className="flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-md bg-amber-950/50 border border-amber-800/50 text-amber-300">
                  <AlertTriangle className="w-3 h-3" />
                  TL committed {tlCommitted} vs {totalLeads} WTF leads
                </div>
              )}
              {verified > 0 && (
                <div className="flex items-center gap-2 text-xs">
                  <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-green-950/50 border border-green-800/50 text-green-300">
                    <CheckCircle2 className="w-3 h-3" />
                    {confirmedCount} confirmed
                  </span>
                  {notReachableCount > 0 && (
                    <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-amber-950/50 border border-amber-800/50 text-amber-300">
                      <Clock className="w-3 h-3" />
                      {notReachableCount} not reachable
                    </span>
                  )}
                  {negativeCount > 0 && (
                    <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-red-950/50 border border-red-800/50 text-red-300">
                      <XCircle className="w-3 h-3" />
                      {negativeCount} negative
                    </span>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Separator */}
          <div className="h-px bg-slate-700/60 -mx-5" />
        </DialogHeader>

        {/* ── Body ── */}
        <div className="flex-1 overflow-auto">
          {loading && (
            <div className="flex items-center justify-center py-20">
              <div className="text-center space-y-3">
                <RefreshCw className="w-5 h-5 animate-spin mx-auto text-slate-500" />
                <p className="text-sm text-slate-500">Fetching leads from WTF...</p>
              </div>
            </div>
          )}

          {error && !loading && (
            <div className="flex items-center justify-center py-20">
              <div className="text-center space-y-3">
                <AlertTriangle className="w-7 h-7 mx-auto text-amber-500" />
                <p className="text-sm text-slate-400">{error}</p>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={fetchLeads}
                  className="bg-slate-800 border-slate-700 text-slate-300 hover:bg-slate-700"
                >
                  Retry
                </Button>
              </div>
            </div>
          )}

          {!loading && !error && leads.length === 0 && (
            <div className="flex items-center justify-center py-20">
              <div className="text-center space-y-3">
                <Users className="w-7 h-7 mx-auto text-slate-600" />
                <p className="text-sm text-slate-500">No closure-expected leads found in WTF for this gym on {date}</p>
                {tlCommitted > 0 && (
                  <div className="flex items-center gap-1.5 justify-center text-xs text-amber-400 mt-2">
                    <AlertTriangle className="w-3.5 h-3.5" />
                    TL committed {tlCommitted} closures but WTF has 0 matching leads
                  </div>
                )}
              </div>
            </div>
          )}

          {!loading && !error && leads.length > 0 && (
            <table className="w-full border-collapse text-sm">
              <thead className="sticky top-0 z-10">
                <tr className="bg-slate-800/90 backdrop-blur-sm border-b border-slate-700/60">
                  <th className="text-left px-3 py-2.5 text-[11px] font-semibold text-slate-500 uppercase tracking-wider w-8">#</th>
                  <th className="text-left px-3 py-2.5 text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Customer</th>
                  <th className="text-left px-3 py-2.5 text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Phone</th>
                  <th className="text-left px-3 py-2.5 text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Receptionist</th>
                  <th className="text-left px-3 py-2.5 text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Added</th>
                  <th className="text-right px-3 py-2.5 text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Value</th>
                  <th className="text-left px-3 py-2.5 text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Followup Status</th>
                  <th className="text-center px-3 py-2.5 text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Calls Out</th>
                  <th className="text-center px-3 py-2.5 text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Connected</th>
                  <th className="text-right px-3 py-2.5 text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Verification</th>
                </tr>
              </thead>
              <tbody>
                {leads.map((lead, idx) => (
                  <LeadVerifyRow
                    key={`${lead.phone}-${idx}`}
                    lead={lead}
                    idx={idx}
                    gym={gym}
                    date={date}
                    onVerified={fetchLeads}
                  />
                ))}
              </tbody>
            </table>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CustomerListDialog;
